import { useState } from "react";
import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { StickyCTA } from "@/components/StickyCTA";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { ArrowRight, Car, Wrench, Calendar, PartyPopper, Store } from "lucide-react";
import { SEOHead } from "@/features/shared/SEOHead";
import { Link } from "react-router-dom";
import { useRevealObserver } from "@/hooks/useRevealObserver";

const CaseStudies = () => {
  useRevealObserver();

  const cases = [
    {
      client: "Need A Car",
      industry: "Automotive",
      location: "Ontario",
      title: "Scaling to 6 Rooftops with a Unified Growth System",
      description: "Before Frayze, the founder unified 150k contacts, 72 staff, and 6 languages into one revenue engine for a multi-location auto group (an employer, not a Frayze client).",
      result: "$1.7M Marketing Savings",
      founderLabel: "Founder build · Toronto Auto Group",
      icon: Car,
      logo: "/images/clients/need-a-car-logo.webp",
      image: "/images/needacar.webp",
      href: "/case-studies/need-a-car-automotive-growth/"
    },
    {
      client: "South Core Roar",
      industry: "Events",
      location: "Thunder Bay, ON",
      title: "Event Platform & Custom App for Fort William Car Show",
      description: "Complete event platform with custom mobile app, vendor management, sponsorship funnels, and ticketing for Thunder Bay's premier motorcycle, car show, and music event.",
      result: "Full Event System",
      icon: PartyPopper,
      logo: "/images/clients/south-core-roar.webp",
      href: "/case-studies/south-core-roar-event-platform/"
    },
    {
      client: "The Motoring District",
      industry: "Automotive",
      location: "Greater Sudbury, ON",
      title: "A Custom Inventory Engine for a Three-in-One Dealership",
      description: "Inventory engine, CRM and speed-to-lead for a family pre-owned dealer running sales, service and detailing: 59 web leads in three months, and 28% of past customers replied to a reactivation campaign.",
      result: "6-Second Median First Reply",
      icon: Car,
      logo: "/images/motoring-district-logo.png",
      href: "/case-studies/the-motoring-district-inventory-engine/"
    },
    {
      client: "Global Aluminum Solutions",
      industry: "Contractor",
      location: "Vaughan, ON",
      title: "Lead Generation System for Railings & Frameless Glass",
      description: "Built a comprehensive lead generation and local SEO system for a contractor specializing in aluminum railings and frameless glass installations.",
      result: "Lead-to-booked-quote: ~12% → 34%",
      icon: Wrench,
      logo: "/images/clients/aluminum-solutions.webp",
      image: "/images/aluminumsolutions.webp",
      href: "/case-studies/global-aluminum-lead-generation/"
    },
    {
      client: "Centennial Family Foods",
      industry: "Retail",
      location: "Thunder Bay, ON",
      title: "From Paper Flyers to a Multi-Channel Retail System",
      description: "Replaced the weekly paper flyer with a digital system: new website, email + SMS distribution, owned database, local SEO, and managed ads for an independent Thunder Bay grocer.",
      result: "100% Weekly Automated",
      icon: Store,
      logo: "/images/clients/centennial-family-foods.svg",
      image: "/images/centennial-family-foods.png",
      href: "/case-studies/centennial-family-foods-retail-system/"
    },
    {
      client: "JC Training & Development",
      industry: "Professional Services",
      location: "Myrtle Beach, SC",
      title: "Eliminating Missed Calls & Automating Scheduling",
      description: "Stopped a busy training provider from losing leads to voicemail and automated their entire booking flow with CRM and communication tools.",
      result: "Missed call capture system",
      icon: Calendar,
      logo: "/images/clients/jc-training-logo.webp",
      href: "/case-studies/jc-training-professional-services/"
    },
  ];

  return (
    <div className="min-h-screen bg-[#FBFAF7] text-[#101319] font-sans antialiased overflow-x-hidden">
      <Header />
      <SEOHead
        title="Case Studies: Real Client Results | Frayze"
        description="See how Frayze helps contractors, auto dealers, and service businesses win more leads and revenue. Real results from real businesses across Canada."
        keywords="Frayze case studies, Thunder Bay business success, contractor lead generation, automotive CRM Canada, local service business systems"
        ogTitle="Case Studies | Proven Business Results | Frayze"
        ogDescription="Real builds. Founder track record included: labeled as such. Featured case studies across industries."
        canonical="https://frayze.ca/case-studies/"
        robots="index, follow"
      />

      <main className="pt-40 pb-16">
        {/* Hero Section */}
        <section className="relative py-20 md:py-28 overflow-hidden bg-[#101319] text-white">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#2F5FE8]/10 to-transparent" />
          <div className="max-w-[1312px] mx-auto px-6 relative z-10">
            <div className="text-center">
              <div className="inline-flex items-center gap-2.5 font-mono-ibm text-xs tracking-[0.16em] uppercase mb-6 text-[#7EA0F5]">
                <span className="w-6 h-[1px] bg-[#2F5FE8]"></span>
                Featured Work
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-[68px] lg:text-[72px] leading-[1.02] tracking-[-0.04em] font-semibold text-white text-pretty mb-6">
                Real builds. Founder track record included.
              </h1>
              <p className="text-lg md:text-xl leading-relaxed text-[#B9C1CE] text-pretty max-w-2xl mx-auto">
                Six featured projects that shaped how we build. Founder's pre-Frayze work is labeled as such.
              </p>
            </div>
          </div>
        </section>

        {/* Case Studies Grid */}
        <section className="bg-[#FBFAF7] py-16 md:py-28 border-b border-[#E9E6DE]">
          <div className="max-w-[1312px] mx-auto px-6">
            <div className="grid md:grid-cols-3 gap-6">
              {cases.map((study, index) => (
                <Card key={index} className={`reveal ${index % 2 === 1 ? "reveal-delay-1" : ""} lift-hover group flex flex-col overflow-hidden hover:shadow-xl hover:shadow-[#2F5FE8]/5 hover:border-[#2F5FE8]/50 transition-all duration-300 border-[#E9E6DE] bg-white rounded-2xl`}>
                  <Link to={study.href} className="block aspect-[16/10] overflow-hidden relative">
                    {study.image ? (
                      <img
                        src={study.image}
                        alt={`${study.client}: ${study.title}`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#2F5FE8]/5 via-[#FBFAF7] to-[#2F5FE8]/5 p-8">
                        <img
                          src={study.logo}
                          alt={`${study.client} logo`}
                          className="max-h-14 max-w-[60%] object-contain opacity-90 transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className="absolute top-3 left-3 inline-flex items-center rounded-full bg-white/95 backdrop-blur px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-[#101319] shadow-sm">
                      {study.industry}
                    </div>
                  </Link>
                  <CardHeader className="pb-3">
                    <div className="text-xs text-[#8A8FA0] mb-2">{study.location}</div>
                    {study.founderLabel && (
                      <div className="text-[11px] font-mono-ibm tracking-[0.12em] uppercase text-[#2F5FE8] mb-2">
                        {study.founderLabel}
                      </div>
                    )}
                    <CardTitle className="text-xl mb-2 text-[#101319]">{study.client}</CardTitle>
                    <CardDescription className="text-sm font-medium text-[#5A6270]">
                      {study.title}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-grow py-0">
                    <p className="text-[#5A6270] mb-4 text-sm">
                      {study.description}
                    </p>
                  </CardContent>
                  <CardFooter className="flex flex-col gap-4 pt-4 pb-4 border-t border-[#E9E6DE] bg-[#FBFAF7]/40">
                    <div className="w-full text-center text-sm font-bold text-[#1B9E6B]">
                      {study.result}
                    </div>
                    <Button className="w-full bg-[#2F5FE8] hover:bg-[#2049BE] text-white font-bold rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98]" asChild>
                      <Link to={study.href} className="flex items-center justify-center gap-2">
                        Read Case Study
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 md:py-32 bg-[#101319] text-white">
          <div className="max-w-[1312px] mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">Ready to build your next system?</h2>
            <p className="text-lg text-[#8A8FA0] mb-10 max-w-2xl mx-auto">
              Book a free strategy call and we'll show you exactly how these same frameworks can grow your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book-consultation" className="text-center font-bold px-8 py-4 rounded-xl shadow-lg shadow-[#2F5FE8]/25 transition-all hover:scale-[1.02] active:scale-[0.98] bg-[#2F5FE8] hover:bg-[#2049BE] text-white">
                Book Your Strategy Call
              </Link>
              <Link to="/" className="text-center border border-white/20 hover:border-white/50 text-white font-bold px-8 py-4 rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98]">
                Back to Home
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <StickyCTA />
    </div>
  );
};

export default CaseStudies;
